import { useMemo } from 'react';

import {
  TActivationTexts,
  TChangePasswordTexts,
  TLoginTexts,
  TOAuthButtonsTexts,
  TOnboardTexts,
  TRegisterTexts,
} from '@skillarea/lib-frontend';
import { Trans, useTranslation } from 'react-i18next';

export const useChangePasswordTexts: () => TChangePasswordTexts = () => {
  const { i18n, t } = useTranslation('onboard');
  return useMemo(() => {
    return {
      success: t('change.success'),
      submit: t('change.submit'),
      subTitle: <Trans i18nKey="change.subTitle" ns="onboard" />,
      confirm: t('change.confirm'),
      new: t('change.new'),
      title: t('change.title'),
      back: t('back'),
      tooltipText: t('passwordNew.tooltip.text', { ns: 'account' }),
      tooltipTitle: t('passwordNew.tooltip.title', { ns: 'account' }),
    };
  }, [i18n.language, t]);
};

export const useLoginTexts = (): TLoginTexts => {
  const { i18n, t } = useTranslation('onboard');
  return useMemo(
    () => ({
      logInTitle: t('logIn.title'),
      restoreTitle: t('restore.title'),
      restoreSubTitle: <Trans i18nKey="restore.subTitle" ns="onboard" />,
      emailPlaceholder: t('email'),
      passwordPlaceholder: t('password'),
      forgot: t('forgot'),
      loginSubmit: t('login'),
      restoreSend: t('restore.send'),
      restoreSendAgain: t('restore.sendAgain'),
      back: t('back'),
      noAccount: t('noAccount'),
      createNew: t('createNew'),
      toastSuccess: t('restore.success'),
      invalidTwoFactorCode: t('invalidTwoFactorCode'),
      staleMfaChallenge: t('staleMfaChallenge'),
    }),
    [i18n.language, t],
  );
};

export const useRegisterTexts = (): TRegisterTexts => {
  const { i18n, t } = useTranslation('onboard');
  return useMemo(
    () => ({
      title: t('register.title'),
      emailPlaceholder: t('email'),
      namePlaceholder: t('name'),
      passwordPlaceholder: t('password'),
      passwordRepeatPlaceholder: t('passwordRepeat'),
      create: t('register.create'),
      hasAccount: t('register.hasAccount'),
      login: t('login'),
      bySign: t('register.bySign'),
      policy: t('register.policy'),
      emailAlreadyUsed: t('register.emailAlreadyUsed'),
      tooltipText: t('passwordNew.tooltip.text', { ns: 'account' }),
      tooltipTitle: t('passwordNew.tooltip.title', { ns: 'account' }),
    }),
    [i18n.language, t],
  );
};

export const useActivationTexts = (): TActivationTexts => {
  const { i18n, t } = useTranslation('onboard');
  return useMemo(
    () => ({
      title: t('activation.title'),
      subTitle: <Trans i18nKey="activation.subTitle" ns="onboard" />,
      submit: t('activation.submit'),
      back: t('back'),
      resend: t('activation.resend'),
      in: t('activation.in'),
      newSent: t('activation.newSent'),
    }),
    [i18n.language, t],
  );
};

export const useOAuthButtonsTexts = (): TOAuthButtonsTexts => {
  const { i18n, t } = useTranslation('onboard');
  return useMemo(
    () => ({
      socials: {
        google: t('socials.google'),
        facebook: t('socials.facebook'),
        discord: t('socials.discord'),
      },
      signUpWith: t('signUpWith'),
      signInWith: t('signInWith'),
      or: t('or'),
    }),
    [i18n.language, t],
  );
};

export const useOnboardText: () => TOnboardTexts = () => {
  const activation = useActivationTexts();
  const changePassword = useChangePasswordTexts();
  const login = useLoginTexts();
  const register = useRegisterTexts();
  const oauth = useOAuthButtonsTexts();

  return useMemo(
    () => ({
      activation,
      changePassword,
      login,
      register,
      oauth,
    }),
    [activation, changePassword, login, register, oauth],
  );
};
